import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useFilteredCommunes } from '../../../hooks/useFilteredCommunes';
import { useParameters } from '../../../hooks/useParameters';
import { useValidations } from '../../../hooks/useValidations';
import { useIdentificationTypeLoader } from '../../../views/updateStudentFile/loaders/IdentificationTypeLoader';
import { SectionTitle } from '../../text';
import useClearInputs from '../hooks/useClearInputs';
import { DynamicContactType } from './RenderSections';

const CHILE_LOWER_CASE = 'chile';

type EmergencyContactPDISectionProps = {
  prefix: string;
  type: DynamicContactType;
  optionalFields?: string[];
  fieldNames?: {
    names: string;
    identificationType: string;
    identification: string;
    passportCountry: string;
    addressStreet: string;
    addressNumber: string;
    addressExtra: string;
    country: string;
    region: string;
    commune: string;
    phone: string;
    cellPhone: string;
    officePhone: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicEmergencyContactPDI = ({
  prefix,
  type,
  optionalFields = [],
  fieldNames = {
    names: 'emergencyNames',
    identificationType: 'emergencyIdentificationType',
    identification: 'emergencyIdentification',
    passportCountry: 'emergencyPassportCountry',
    addressStreet: 'emergencyAddressStreet',
    addressNumber: 'emergencyAddressNumber',
    addressExtra: 'emergencyAddressExtra',
    country: 'emergencyCountry',
    region: 'emergencyRegion',
    commune: 'emergencyCommune',
    phone: 'emergencyPhone',
    cellPhone: 'emergencyCellPhone',
    officePhone: 'emergencyOfficePhone',
  },
  disabled = false,
}: EmergencyContactPDISectionProps) => {
  const { t } = useTranslation();
  const { control, watch, formState, setValue } = useFormContext();
  const { validateTextNotEmpty, validatePhone } = useValidations();
  const { regionOptions, countryOptions } = useParameters();
  const { data: identificationTypes } = useIdentificationTypeLoader();

  const [rut, ownSustainer, selectedRegion, identificationType] = watch([
    'rut',
    'ownSustainer',
    fieldNames.region,
    fieldNames.identificationType,
  ]);

  const filteredCommunes = useFilteredCommunes(selectedRegion);
  const isRegionTouched = formState.touchedFields[fieldNames.region];

  const optional = useCallback(
    (value: string, fieldName: string) => {
      if (optionalFields.includes(fieldName)) {
        return undefined;
      }
      return validateTextNotEmpty(value);
    },
    [optionalFields, validateTextNotEmpty],
  );

  const handleChangeOwnSustainer = useCallback(
    (next: boolean) => {
      if (!next) {
        setValue(fieldNames?.addressStreet, '');
        setValue(fieldNames?.addressNumber, '');
        setValue(fieldNames?.addressExtra, '');
        setValue(fieldNames?.region, null);
        setValue(fieldNames?.commune, null);
        setValue(fieldNames?.phone, '');
        setValue(fieldNames?.cellPhone, '');
      }
    },
    [
      fieldNames?.addressStreet,
      fieldNames?.addressNumber,
      fieldNames?.addressExtra,
      fieldNames?.region,
      fieldNames?.commune,
      fieldNames?.phone,
      fieldNames?.cellPhone,

      setValue,
    ],
  );

  useClearInputs({ type, handleChangeOwnSustainer: handleChangeOwnSustainer });

  useEffect(() => {
    if (rut) {
      setValue(
        fieldNames.country,
        countryOptions.find(
          (c) => c.label.toLocaleLowerCase() === CHILE_LOWER_CASE,
        ),
      );
    }
  }, [countryOptions, fieldNames.country, rut, setValue]);

  useEffect(() => {
    if (isRegionTouched) {
      setValue(fieldNames.commune, null);
    }
  }, [selectedRegion, setValue, isRegionTouched, fieldNames.commune]);

  // Memo
  const identificationTypeSelect = useMemo(() => {
    return (identificationTypes?.identificationTypes ?? []).map(
      (identificationType) => ({
        value: identificationType.id,
        label: identificationType.name,
      }),
    );
  }, [identificationTypes]);

  const isPassport = useMemo(() => {
    return identificationType?.value === 'PASSPORT';
  }, [identificationType]);

  const fields = useMemo(() => {
    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.names}
              label={t(`${prefix}.${fieldNames.names}`)}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.names),
                },
              }}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={isPassport ? 4 : 4}>
            <Select
              name={`${fieldNames.identificationType}`}
              label={t(`${prefix}.${fieldNames.identificationType}`)}
              rules={{ validate: validateTextNotEmpty }}
              options={identificationTypeSelect}
              isClearable={false}
              control={control}
              shouldUnregister={true}
            />
          </Col>
          {isPassport && (
            <Col xs={5} lg={4} className="pr-1 pr-md-2">
              <Select
                name={`${fieldNames.passportCountry}`}
                label={t(`${prefix}.${fieldNames.passportCountry}`)}
                options={countryOptions}
                isClearable={false}
                control={control}
                shouldUnregister={true}
              />
            </Col>
          )}
          <Col className="pb-3" xs={12} lg={isPassport ? 4 : 4}>
            <TextInput
              name={`${fieldNames.identification}`}
              label={t(`${prefix}.${fieldNames.identification}`)}
              rules={{ validate: validateTextNotEmpty }}
              control={control}
              shouldUnregister={true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={8}>
            <TextInput
              name={fieldNames.addressStreet}
              label={t(`${prefix}.${fieldNames.addressStreet}`)}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.addressStreet),
                },
              }}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.addressNumber}
              label={t(`${prefix}.${fieldNames.addressNumber}`)}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.addressNumber),
                },
              }}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.addressExtra}
              label={t(`${prefix}.${fieldNames.addressExtra}`)}
              control={control}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>

          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.region}
              label={t(`${prefix}.${fieldNames.region}`)}
              options={regionOptions}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.region),
                },
              }}
              disabled={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.commune}
              label={t(`${prefix}.${fieldNames.commune}`)}
              options={filteredCommunes}
              control={control}
              rules={{
                validate: {
                  require: (value) => optional(value, fieldNames.commune),
                },
              }}
              shouldUnregister
              disabled={disabled || ownSustainer === true}
            />
          </Col>

          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.phone}
              label={t(`${prefix}.${fieldNames.phone}`)}
              control={control}
              formatter="phone"
              rules={{ validate: validatePhone }}
              placeholder={t(`common.placeholder.phone`)}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.cellPhone}
              label={t(`${prefix}.${fieldNames.cellPhone}`)}
              control={control}
              formatter="phone"
              rules={{
                validate: {
                  phone: validatePhone,
                  require: (value) => optional(value, fieldNames.cellPhone),
                },
              }}
              placeholder={t(`common.placeholder.cellphone`)}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.officePhone}
              label={t(`${prefix}.${fieldNames.officePhone}`)}
              control={control}
              formatter="phone"
              rules={{
                validate: {
                  phone: validatePhone,
                  require: (value) => optional(value, fieldNames.officePhone),
                },
              }}
              placeholder={t(`common.placeholder.officePhone`)}
              readOnly={disabled || ownSustainer === true}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [
    disabled,
    fieldNames.names,
    fieldNames.identificationType,
    fieldNames.identification,
    fieldNames.passportCountry,
    fieldNames.addressExtra,
    fieldNames.addressNumber,
    fieldNames.addressStreet,
    fieldNames.region,
    fieldNames.commune,
    fieldNames.phone,
    fieldNames.cellPhone,
    fieldNames.officePhone,
    t,
    optional,
    validatePhone,
    validateTextNotEmpty,
    identificationTypeSelect,
    isPassport,
    ownSustainer,
    regionOptions,
    countryOptions,
    filteredCommunes,
    control,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicEmergencyContactPDI;
